import { useEffect, useState } from 'react'

import { ApiError } from '@/lib/api'

/**
 * Comprobantes y evidencias.
 *
 * Tres cosas separan este módulo del resto del cliente:
 *
 * 1. **El cuerpo va como `FormData`, sin `Content-Type` a mano.** El navegador
 *    tiene que escribir esa cabecera él solo porque incluye el `boundary` que
 *    separa las partes del formulario. Ponerla nosotros la deja sin `boundary`
 *    y el servidor recibe un cuerpo que no puede partir: el error llega como un
 *    400 críptico y cuesta horas encontrarlo.
 *
 * 2. **El archivo no se sirve por URL pública.** `GET /api/attachments/:id` pide
 *    la sesión, así que para mostrar una imagen hay que descargarla con
 *    `credentials: 'include'` y envolver el blob en un `objectURL`. Un
 *    `<img src="/api/attachments/…">` funcionaría hoy en el navegador, pero
 *    dentro de Capacitor la app corre en `capacitor://localhost` y la API en
 *    otro origen: ahí una subcarga no lleva la cookie y la imagen sale rota.
 *    `fetch` con `credentials: 'include'` sí la lleva en los dos casos.
 *
 * 3. **Cada `objectURL` se revoca.** Un `objectURL` retiene el blob entero en
 *    memoria hasta que se revoca explícitamente; abrir diez comprobantes sin
 *    revocarlos deja diez fotos vivas en una app que la gente no cierra nunca.
 *    Por eso la vista previa vive en un hook, que es lo que sabe cuándo se
 *    desmonta.
 */

/** 10 MB, el mismo tope que aplica el servidor. */
export const MAX_ADJUNTO_BYTES = 10 * 1024 * 1024

/**
 * Lo que el servidor acepta, con la extensión que le corresponde.
 *
 * La tabla está duplicada a propósito con la del servidor: aquí sirve para
 * rechazar antes de subir —y decir por qué— y allá para no fiarse de nosotros.
 * Si alguna vez divergen, la del servidor manda; esta solo se adelanta.
 */
export const TIPOS_ACEPTADOS: Readonly<Record<string, string>> = {
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
  'application/pdf': '.pdf',
  'text/csv': '.csv',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
}

/**
 * Valor de `accept` cuando se admite cualquier evidencia.
 *
 * Van los tipos **y** las extensiones. Varios gestores de archivos de Android
 * filtran por extensión y no por tipo: solo con la lista de MIME, un `.xlsx` del
 * banco aparece en gris y no se puede elegir, que es justo el archivo que se
 * quiere adjuntar a una revisión.
 */
export const ACCEPT_TODO = [
  ...Object.keys(TIPOS_ACEPTADOS),
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.pdf',
  '.csv',
  '.xls',
  '.xlsx',
].join(',')

/**
 * Extensión → tipo, para rellenar el hueco cuando el navegador no declara nada.
 *
 * Pasa de verdad: un `.csv` exportado del banco llega con `type` vacío en varios
 * Android, y un archivo sin tipo lo rechaza el servidor con un 415 que no dice
 * nada útil. Solo se usa para **rellenar**, nunca para corregir lo que el
 * navegador sí declaró.
 */
const TIPO_POR_EXTENSION: Readonly<Record<string, string>> = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
  '.pdf': 'application/pdf',
  '.csv': 'text/csv',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

/** Frase que enumera lo aceptado, para los mensajes de rechazo. */
const LISTA_LEGIBLE =
  'Se aceptan fotos (JPG, PNG o WebP), PDF y hojas de cálculo (CSV, XLS o XLSX).'

// ═══════════════════════════════════════════════════════════════════════════
//  Tipos
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Lo mínimo para dibujar un adjunto en pantalla.
 *
 * Existe porque el servidor devuelve el mismo archivo con dos formas: completa
 * al subirlo, y recortada dentro de una revisión —donde `reviewId` sobra, que
 * es la revisión misma—. Las vistas solo necesitan esto, así que piden esto y
 * sirven para las dos.
 */
export interface AdjuntoVisible {
  id: string
  fileName: string
  mimeType: string
  sizeBytes: number
}

/** Metadatos de un archivo ya subido. El contenido nunca viaja en el JSON. */
export interface Adjunto extends AdjuntoVisible {
  /** Uno de los dos siempre es nulo: un archivo cuelga de un sitio, no de dos. */
  transactionId: string | null
  reviewId: string | null
  /** Solo en evidencias de revisión: el monto que ese papel demuestra. */
  declaredAmountCents: number | null
  uploadedByMemberId: string
  createdAt: string
  /** Ruta de la API, no del disco. Solo abre con la sesión puesta. */
  url: string
}

/** A qué se pega el archivo. El servidor rechaza que vengan los dos. */
export type DestinoAdjunto = { transactionId: string } | { reviewId: string }

// ═══════════════════════════════════════════════════════════════════════════
//  Validación previa
// ═══════════════════════════════════════════════════════════════════════════

/** Tipo declarado, en minúsculas y sin los parámetros del `charset`. */
function tipoDeclarado(file: File): string {
  const [tipo = ''] = file.type.split(';')
  return tipo.trim().toLowerCase()
}

function extensionDe(nombre: string): string {
  const punto = nombre.lastIndexOf('.')
  return punto === -1 ? '' : nombre.slice(punto).toLowerCase()
}

/**
 * Tipo con el que se va a subir el archivo.
 *
 * Si el navegador declaró uno, ese manda —aunque no lo aceptemos, porque
 * entonces lo que toca es rechazarlo con su nombre y no disfrazarlo. Solo
 * cuando no declara nada se deduce de la extensión.
 */
export function tipoDeSubida(file: File): string {
  const declarado = tipoDeclarado(file)
  if (declarado !== '') return declarado
  return TIPO_POR_EXTENSION[extensionDe(file.name)] ?? ''
}

/** Tamaño en unidades humanas. Un «10485760» no le dice nada a nadie. */
export function formatearTamano(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
}

export function esImagen(mimeType: string): boolean {
  return mimeType === 'image/jpeg' || mimeType === 'image/png' || mimeType === 'image/webp'
}

/**
 * Motivo por el que un archivo no se puede subir, o `null` si sí se puede.
 *
 * Se comprueba en el cliente **antes** de subir para no gastar la subida de 9 MB
 * de una foto que el servidor va a rechazar en el último byte —con datos
 * móviles eso es dinero real— y, sobre todo, para poder decir qué pasa con este
 * archivo concreto en vez de devolver un «error al subir».
 */
export function motivoDeRechazo(file: File): string | null {
  const nombre = file.name || 'el archivo'

  if (file.size === 0) {
    return `«${nombre}» está vacío. Vuelve a tomar la foto o elige otro archivo.`
  }

  const tipo = tipoDeSubida(file)
  if (!(tipo in TIPOS_ACEPTADOS)) {
    const extension = extensionDe(nombre)
    const que = extension ? `un archivo ${extension}` : 'de un tipo que no reconocemos'
    return `«${nombre}» es ${que} y no se puede adjuntar. ${LISTA_LEGIBLE}`
  }

  if (file.size > MAX_ADJUNTO_BYTES) {
    return `«${nombre}» pesa ${formatearTamano(file.size)} y el máximo son 10 MB. Si es una foto, tómala en menor resolución o recórtala.`
  }

  return null
}

// ═══════════════════════════════════════════════════════════════════════════
//  Subida, borrado y descarga
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Sube un archivo y lo pega a un movimiento o a una revisión.
 *
 * `declaredAmountCents` solo aplica a una evidencia de revisión: en un
 * comprobante de gasto el monto ya lo lleva el movimiento, y el servidor
 * rechaza que venga por partida doble.
 */
export async function subirAdjunto(
  file: File,
  destino: DestinoAdjunto & { declaredAmountCents?: number },
): Promise<Adjunto> {
  const motivo = motivoDeRechazo(file)
  if (motivo) throw new ApiError(422, motivo)

  const formulario = new FormData()

  // El tercer argumento fija el nombre del archivo en la parte del formulario;
  // sin él, algunos navegadores mandan «blob» y el comprobante se guarda con ese
  // nombre para siempre.
  const declarado = tipoDeclarado(file)
  const tipo = tipoDeSubida(file)
  formulario.append(
    'file',
    // Volver a envolver el blob solo cuando el navegador no declaró tipo: es la
    // única forma de que la parte lleve un `Content-Type` que el servidor
    // reconozca. Si el navegador sí declaró algo, se respeta.
    declarado === '' ? new File([file], file.name, { type: tipo }) : file,
    file.name,
  )

  if ('transactionId' in destino) {
    formulario.append('transactionId', destino.transactionId)
  } else {
    formulario.append('reviewId', destino.reviewId)
  }
  if (destino.declaredAmountCents !== undefined) {
    formulario.append('declaredAmountCents', String(destino.declaredAmountCents))
  }

  let respuesta: Response
  try {
    respuesta = await fetch('/api/attachments', {
      method: 'POST',
      credentials: 'include',
      // Sin `headers`. El navegador escribe `multipart/form-data; boundary=…`
      // por su cuenta y ese `boundary` es lo que separa las partes: fijar la
      // cabecera a mano lo borra y el servidor recibe un cuerpo ilegible.
      body: formulario,
    })
  } catch {
    throw new ApiError(0, 'Sin conexión. El gasto ya quedó guardado; sube el comprobante más tarde.')
  }

  return leerRespuesta<Adjunto>(respuesta)
}

export async function borrarAdjunto(id: string): Promise<void> {
  let respuesta: Response
  try {
    respuesta = await fetch(`/api/attachments/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    })
  } catch {
    throw new ApiError(0, 'Sin conexión. Revisa tu red e inténtalo de nuevo.')
  }

  if (respuesta.status === 204) return
  await leerRespuesta<void>(respuesta)
}

/** El contenido del archivo, ya con la sesión puesta. */
export async function descargarAdjunto(id: string, signal?: AbortSignal): Promise<Blob> {
  let respuesta: Response
  try {
    respuesta = await fetch(`/api/attachments/${id}`, {
      credentials: 'include',
      signal,
    })
  } catch (error: unknown) {
    // Un abort es la salida normal cuando la vista se cierra antes de que
    // termine la descarga, no un fallo que haya que anunciar.
    if ((error as { name?: string }).name === 'AbortError') throw error
    throw new ApiError(0, 'Sin conexión. No pudimos abrir el archivo.')
  }

  if (!respuesta.ok) {
    const payload = (await respuesta.json().catch(() => null)) as { message?: string } | null
    throw new ApiError(
      respuesta.status,
      payload?.message ?? 'No pudimos abrir el archivo. Inténtalo de nuevo.',
    )
  }

  return respuesta.blob()
}

/**
 * Guarda el archivo en el dispositivo.
 *
 * Pasa por el blob en vez de apuntar un enlace a `/api/attachments/:id` por lo
 * mismo que las imágenes: dentro de Capacitor la descarga directa iría a otro
 * origen y saldría sin cookie. El `objectURL` se revoca en cuanto el navegador
 * toma el archivo.
 */
export async function guardarAdjunto(adjunto: AdjuntoVisible): Promise<void> {
  const blob = await descargarAdjunto(adjunto.id)
  const url = URL.createObjectURL(blob)
  try {
    const enlace = document.createElement('a')
    enlace.href = url
    enlace.download = adjunto.fileName
    enlace.click()
  } finally {
    // Un fotograma de margen: revocar en el mismo tic cancela la descarga en
    // Safari, que todavía no ha leído el blob cuando vuelve del `click`.
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
}

async function leerRespuesta<T>(respuesta: Response): Promise<T> {
  const payload = (await respuesta.json().catch(() => null)) as
    | { message?: string; fieldErrors?: Record<string, string[]> }
    | null

  if (!respuesta.ok) {
    throw new ApiError(
      respuesta.status,
      payload?.message ?? 'No pudimos subir el archivo. Inténtalo de nuevo.',
      payload?.fieldErrors,
    )
  }

  return payload as T
}

// ═══════════════════════════════════════════════════════════════════════════
//  Vista previa
// ═══════════════════════════════════════════════════════════════════════════

export interface VistaPrevia {
  url: string | null
  cargando: boolean
  error: string | null
}

/**
 * `objectURL` del contenido de un adjunto, revocado al desmontar.
 *
 * La revocación es la razón de que esto sea un hook y no una función: el
 * navegador retiene el blob mientras exista la URL, así que cada apertura sin
 * revocar deja una foto entera en memoria. Con `id` en `null` no descarga nada
 * —así la miniatura de un PDF no gasta red— y al cambiar de `id` revoca el
 * anterior antes de pedir el siguiente.
 */
export function useVistaPrevia(id: string | null): VistaPrevia {
  const [estado, setEstado] = useState<VistaPrevia>({ url: null, cargando: id !== null, error: null })

  useEffect(() => {
    if (id === null) {
      setEstado({ url: null, cargando: false, error: null })
      return
    }

    let vigente = true
    let creada: string | null = null
    const control = new AbortController()

    setEstado({ url: null, cargando: true, error: null })

    descargarAdjunto(id, control.signal)
      .then((blob) => {
        creada = URL.createObjectURL(blob)
        if (!vigente) {
          // La vista se cerró mientras descargaba: la URL se revoca aquí porque
          // la limpieza del efecto ya corrió y no la vio nacer.
          URL.revokeObjectURL(creada)
          return
        }
        setEstado({ url: creada, cargando: false, error: null })
      })
      .catch((error: unknown) => {
        if (!vigente) return
        setEstado({
          url: null,
          cargando: false,
          error: error instanceof Error ? error.message : 'No pudimos abrir el archivo.',
        })
      })

    return () => {
      vigente = false
      control.abort()
      if (creada) URL.revokeObjectURL(creada)
    }
  }, [id])

  return estado
}
